import { useState, useEffect, useMemo } from 'react';
import dataServices from "../../services/DataServices";
import Auth from '../../methods/Auth';
import { clearAllStores as clearCsDB } from '../../services/CsDBService';
import { clearAllStores as clearEventsDB } from '../../services/EventsDBService';
import { clearAllStores as clearFilingsDB } from '../../services/FilingsDBService';
import { clearAllStores as clearEsgDB } from '../../services/EsgDBService';
import { clearAllStores as clearEstDB } from '../../services/EstDBService';
import { clearAllStores as clearNewsDB } from '../../services/NewsDBService';
import { clearAllStores as clearSnapshotDB } from '../../services/SnapshotDBService';
import { clearAllStores as clearStreamingDB } from '../../services/StreamingDBService';
import { clearAllStores as clearLipperDB } from '../../services/LipperDBService';
import { clearAllStores as clearOwnershipDB } from '../../services/OwnershipDBService';
import { fetchData, formatDate, getDownloadLink, syncVersions } from './Helpers';
import { Pagination } from '@refinitiv-ui/efx-grid/extensions';
import { info } from '@refinitiv-ui/elements/notification';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TAB_URL = "upload-to-s3";

const companySnapshotCheckboxes = [
  { label: "All Entity Reference Data Items", value:"Entity_Reference", checked: false },
  { label: "All Financial Stmt Data Items", value:"Financial_Statement", checked: false },
  { label: "KPIs", value:"KPIs", checked: false },
  { label: "Segments", value:"Segments", checked: false },
  { label: "Industry-Medians", value:"Industry_Medians", checked: false },
  { label: "Current Analytics", value:"Current_Analytics", checked: false },
  { label: "Price-Volumes Analytics", value:"Price_Volumes_Analytics", checked: false },
  { label: "All Estimates Data Items", value:"All_Estimates_Data_Items", checked: false },
  { label: "Latest & Historical Analytics", value:"Latest_Historical_Analytics", checked: false },
  { label: "Wealth Packages", value:"Wealth_Packages", checked: false },
  { label: "Segmentation Overview Fin Stmt", value:"Segmentation_Overview_Fin_Stmt", checked: false }
];

const eventCheckBoxes = [
  { label: "Event Types", value:"Event_Types", checked: false },
  { label: "Data Fields", value:"Event_Data_Fields", checked: false },
  { label: "Country", value:"Event_Country", checked: false },
  { label: "Industry", value:"Event_Industry", checked: false },
  { label: "Exchanges", value:"Event_Exchanges", checked: false },
  { label: "ECI vs ECIX", value:"Event_ECI_vs_ECIX", checked: false },
  { label: "Packages & Endpoints", value:"Event_Packages_Endpoints", checked: false },
  { label: "Data Display Restrictions", value:"Event_Data_Display_Restrictions", checked: false }
];

const filingsCheckboxes = [
  { label: "Edgar", value: "Filings_Edgar", checked: false },
  { label: "Sections", value: "Filings_Sections", checked: false },
  { label: "High and Mid Level Categories", value: "Filings_Categories", checked: false },
  { label: "CRIS", value: "Filings_Cris", checked: false },
  { label: "SEDAR", value: "Filings_Sedar", checked: false },
  { label: "Tanshin", value: "Filings_Tanshin", checked: false },
  { label: "Yuho", value: "Filings_Yuho", checked: false },
  { label: "OBI", value: "Filings_Obi", checked: false },
  { label: "China", value: "Filings_China", checked: false },
  { label: "DART (Korea)", value: "Filings_Dart", checked: false },
  { label: "Bridge", value: "Filings_Bridge", checked: false },
  { label: "SEC NonEdgar", value: "Filings_SecNonEdgar", checked: false },
  { label: "Italy", value: "Filings_Italy", checked: false },
  { label: "Muni", value: "Filings_Muni", checked: false },
  { label: "Segmentation", value: "Filings_Segmentation", checked: false }
];

const esgCheckboxes = [
  { label: "ESG Coverage", value:"Esg_Coverage", checked: false },
  { label: "Fields in Package", value:"Fields_in_Package", checked: false },
  { label: "Field Description", value:"Field_Description", checked: false },
  { label: "Package-Endpoint Mapping", value:"Package_Endpoint_Mapping", checked: false },
  { label: "PLIs", value:"ESG_PLIs", checked: false }
];

const estimatesCheckboxes = [
  { label: "List of Measures", value:"Est_List_of_Measures", checked: false },
  { label: "Endpoints-Fields", value:"Est_Endpoints_Fields", checked: false },
  { label: "Field Descriptions", value:"Est_Field_Descriptions", checked: false },
  { label: "Wealth Packages", value:"Est_Wealth_Packages", checked: false },
  { label: "Segmentation", value:"Est_Segmentation", checked: false }
];

const newsCheckboxes = [
  { label: "Packages", value: "News_Packages", checked: false },
  { label: "Data Fields", value: "News_Data_Fields", checked: false },
  { label: "RCS Topic Codes", value: "News_RCS_Topic_Codes", checked: false },
  { label: "3rd Party Sources", value: "News_3rd_Party_Sources", checked: false },
  { label: "Reuters News", value: "News_Reuters_News", checked: false },
  { label: "3rd Party News", value: "News_3rd_Party_News", checked: false },
  { label: "Top News", value: "News_Top_News", checked: false },
  { label: "Online Reports", value: "News_Online_Reports", checked: false },
  { label: "Breakingviews", value: "News_Breakingviews", checked: false },
  { label: "Significant Development", value: "News_Significant_Development", checked: false },
  { label: "Japanese Investor Brief", value: "News_Japanese_Investor_Brief", checked: false },
  { label: "Languages", value: "News_Languages", checked: false },
  { label: "News License PLI", value: "News_License_PLI", checked: false }
];

const snapshotCheckboxes = [
  { label: "snapshot_quotes", value:"snapshot_quotes", checked: true }
];

const streamingCheckboxes = [
  { label: "streaming_quotes", value:"streaming_quotes", checked: false },
  { label: "Pricing Services", value:"streaming_pricing_services", checked: false },
  { label: "Endpoints", value:"streaming_endpoints", checked: false }
];

const lipperFundsCheckboxes = [
  { label: "Lipper Funds", value:"Lipper_Funds", checked: false },
  { label: "Geo_Universe_Restrictions", value:"Lipper_Fund_Restrictions", checked: false },
  { label: "PLIs", value:"Lipper_Fund_PLIs", checked: false },
  { label: "Geography", value:"Lipper_Fund_Geography", checked: false }
];

const ownershipCheckboxes = [
  { label: "Ownership", value:"Ownership", checked: false },
  { label: "Countries", value:"Ownership_Countries", checked: false },
  { label: "Field Descriptions", value:"Ownership_Field_Descriptions", checked: false },
  { label: "Stocks", value:"Ownership_Stocks", checked: false },
  { label: "Funds", value:"Ownership_Funds", checked: false }
];

function Upload() {
  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState("0");
  const [progressText, setProgressText] = useState("Processing...");
  const [tabValue, setTabValue] = useState("company_snapshot");
  const [versionValue, setVersionValue] = useState("minor");
  const [fileVersionsData, setFileVersionsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [file, setFile] = useState(null);
  const [fileString, setFileString] = useState("");
  const fileMaps = useMemo(() => ({
    "company_snapshot": "Company Snapshot",
    "events": "Events",
    "filings": "Filings",
    "esg": "ESG",
    "estimates": "Estimates",
    "news": "News",
    // "snapshot_quotes": "Snapshot Quotes",
    "streaming_quotes": "Streaming & Snapshot Quotes",
    "lipper_funds": "Lipper Funds",
    "ownership": "Ownership"
  }), []);

  useEffect(() => {
    fetchData().then(res => {
      const reversedData = Array.isArray(res) ? [...res].reverse() : [];
      setFileVersionsData(reversedData);
      setFilteredData(reversedData.filter(item => item.filename === "company_snapshot"));
      setLoading(false);
    });
  }, []);

  function onNextClick() {
    const dialog = document.getElementById("d1");
    const cBoxes = document.getElementsByClassName("admin-checkboxes");
    const createdBy = Auth.currentUser.name[0] +" "+ Auth.currentUser.name[1];
    let s = "";
    for(let checkbox of cBoxes){
      if(checkbox.checked){
        s += "1";
      }else{
        s += "0";
      }
    }
    setFileString(s);
    
    if (dialog) {
      dialog.opened = false;
      const uploadDialog = document.getElementById("upload-confirmation-dlg");
      if (uploadDialog) {
        uploadDialog.opened = true;
        uploadDialog.addEventListener("cancel", (event) => {
          uploadDialog.opened = false;
        });
        uploadDialog.addEventListener("confirm", (event) => {
          uploadFile(file, tabValue+"-"+s+"-"+versionValue+"-"+createdBy);
        });
      }
    }
  }

  function uploadClick(){
    const dialog = document.getElementById("d1");
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".xlsx";
    fileInput.onchange = (e) => {
      const localFile = e.target.files[0];
      if (localFile) {
        setFile(localFile);
        if (dialog) {
          dialog.opened = true;
        }
      }
    };
    fileInput.click();
  }

  function clearAllDBStores() {
    const dialog = document.getElementById("dlg1");
    if (dialog) {
      dialog.opened = true;
      dialog.addEventListener("cancel", (event) => {
        dialog.opened = false;
      });
      dialog.addEventListener("confirm", (event) => {
        clearCsDB();
        clearEventsDB();
        clearFilingsDB();
        clearEsgDB();
        clearEstDB();
        clearNewsDB();
        clearSnapshotDB();
        clearStreamingDB();
        clearLipperDB();
        clearOwnershipDB();
      });
    }
  }

  const downloadFile = (s3Path) => {
    setLoading(true);
    getDownloadLink(s3Path).then(link => {
      const a = document.createElement("a");
      a.href = link.downloadUrl;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      info("File downloaded successfully", 5000);
      setLoading(false);
    }).catch(console.error).finally(() => setLoading(false));
  };

  const sheetMappings = (tab, sheetId) => {
    const sheetMaps = {
      "company_snapshot": companySnapshotCheckboxes,
      "events": eventCheckBoxes,
      "filings": filingsCheckboxes,
      "esg": esgCheckboxes,
      "estimates": estimatesCheckboxes,
      "news": newsCheckboxes,
      "snapshot_quotes": snapshotCheckboxes,
      "streaming_quotes": streamingCheckboxes,
      "lipper_funds": lipperFundsCheckboxes,
      "ownership": ownershipCheckboxes
    };
    
    let sheetNames = "";
    for (let i = 0; i < sheetId.length; i++){
      if(sheetId[i] === "1"){
        let sheetName = sheetMaps[tab][i].label;
        sheetNames += sheetName + ",";
      }
    }
    return sheetNames.slice(0, -1);
  };

  const textWithPill = (e) => {
    var cell = e.cell;
    var content = cell.getContent();
    var pill;
    if (!content) {
      content = document.createElement("div");
      pill = document.createElement("ef-pill");
      pill.style.color = "#fff";
      pill.style.borderRadius = "3px";
      pill.style.padding = "10px 20px";
      pill.style.width = "100px";
      pill.style.height = "30px";
      pill.style.fontSize = "14px";
      pill.style.fontWeight = "bold";
      pill.style.textTransform = "capitalize";
      pill.style.border = "none";
      content.appendChild(pill);
    } else {
      pill = content.querySelector("ef-pill");
      if (!pill) {
        pill = document.createElement("ef-pill");
        pill.style.color = "#fff";
        pill.style.borderRadius = "3px";
        pill.style.padding = "10px 20px";
        pill.style.width = "100px";
        pill.style.height = "30px";
        pill.style.fontSize = "14px";
        pill.style.fontWeight = "bold";
        pill.style.textTransform = "capitalize";
        pill.style.border = "none";
        content.appendChild(pill);
      }
    }

    pill.textContent = e.data;
    pill.style.backgroundColor = e.data === "active" ? "#00A000" : "#E41C0E";

    cell.setContent(content);
  };

  useEffect(() => {
    const tabSelect = document.getElementById("tabSelect");
    tabSelect.addEventListener("value-changed", (e) => {
      setFilteredData(fileVersionsData.filter(item => item.filename === e.target.value));
    });

  }, [fileVersionsData]);

  useEffect(() => {
    const today = new Date();
    const options = { month: 'short', day: 'numeric' };
    const formattedDate = today.toLocaleDateString('en-US', options);
    const dateElement = document.getElementById("date_p");

    if (dateElement) {
      dateElement.textContent = `Today, ${formattedDate}`;
    }

    const textWithIcon = (e) => {
      var cell = e.cell;
      var content = cell.getContent();
      if (!content) {
        content = document.createElement("div");
        content.className = 'icon-cell';
        content.style.textAlign = "center";

        let btn1 = document.createElement("ef-button");
        btn1.className = "icon-cell_btn";
        btn1.style.backgroundColor = "#001EFF";
        btn1.style.color = "white";
        btn1.style.borderRadius = "3px";
        btn1.style.border = "none";
        btn1.icon = "download";
        content.appendChild(btn1);
        cell.setContent(content);
      }

      const value = e.data;
      const iconBtn = content.querySelector(".icon-cell_btn");
      iconBtn.onclick = async () => {
        downloadFile(value);
      };
    };

    const columns = [
      { name: 'FILENAME', field: 'filename', width: 100, scalable: true },
      { name: 'Sheets', field: 'sheets', width: 300, scalable: true },
      { name: 'Uploaded By', field: 'uploadedBy', width: 100, scalable: true },
      { name: 'Uploaded At', field: 'createdAt', width: 100, scalable: true },
      { name: 'Version', field: 'version', width: 50, scalable: true },
      { name: 'Status', field: 'status', renderer: textWithPill, width: 80, scalable: true },
      { name: 'Actions', field: 'actions', width: 60, scalable: true, renderer: textWithIcon }
    ];

    const transformData = (data) => {
      return data.map(item => ({
        filename: fileMaps[item.filename] || item.filename,
        sheets: sheetMappings(item.filename, item.sheets),
        uploadedBy: item.uploadedBy,
        createdAt: formatDate(item.createdAt),
        version: item.version,
        status: item.status,
        actions: item.s3_path
      }));
    };
    
    const grid = document.getElementById('filesGrid');
    grid.config = {
      rowHeight: 60,
      headerRowHeight: 40,
      columns: columns,
      dataModel: {
        data: transformData(filteredData)
      },
      pagination: {
        element: document.getElementById("pagination"),
        page: 1,
        pageSize: 10
      },
      extensions: [
        new Pagination()
      ]
    };
  },[filteredData, fileMaps])

  useEffect(() => {
    if (loader) {
      const progressBar = document.getElementById("progress-bar");
      const progressTextElement = document.getElementById("progress-text");
      if (progressTextElement) {
        progressTextElement.textContent = progressText;
      }
      if (progressBar) {
        progressBar.value = `${progress}`;
        progressBar.label = `${progress}%`;
      }
    }
  }, [loader, progress, progressText]);

  const CheckStatus = (jobId) => {
    const apiUrl = `${API_BASE_URL}${TAB_URL}?job_id=${jobId}`;
    dataServices.sendGetRequest(apiUrl).then((response) => {
      if (response.status === "OK") {
        const status = response.data["status"];
        console.log("Job Status:", status);
        console.log(response.data["error"]);
        setProgress(String(response.data["progress"]));
        setProgressText(response.data["error"] || "Processing...");
        if (status === "SUCCESS") {
          console.log("Job completed successfully.");
          setProgress(String(response.data["progress"]));
          setProgressText(response.data["error"] || "Processing...");
          syncVersions().then(() => {
            info("File processed and data synced successfully", 5000);
            fetchData().then(res => {
              const reversedData = Array.isArray(res) ? [...res].reverse() : [];
              setFileVersionsData(reversedData);
              setFilteredData(reversedData.filter(item => item.filename === "company_snapshot"));
              setLoading(false);
            });
          });
          setTimeout(() => setLoader(false), 5000);
          return;
        } else if (status === "FAILED") {
          console.error("Job failed.");
          setProgress(String(response.data["progress"]));
          setProgressText(response.data["error"] || "Processing...");
          setTimeout(() => setLoader(false), 5000);
          return;
        } else {
          // If still processing, check again after a delay
          setTimeout(() => CheckStatus(jobId), 1000);
        }
      } else {
        console.error("Error fetching job status:", response.error);
        setLoader(false);
        return;
      }
    });
  };

  const uploadFile = (file, fname) => {
    setLoader(true);
    setProgressText("Checking status...");
    setProgress("0");
    const form = new FormData();
    console.log(fname);
    
    form.append("file", file, `${fname}.xlsx`);
    const apiUrl = `${API_BASE_URL}${TAB_URL}`;
    const jwt = localStorage.getItem('jwt')
    const headers = {
      // 'Content-Type': "multipart/form-data"
    }
    if (jwt != null){
      headers["Authorization"] = jwt
    }

    fetch(apiUrl, {
      method: "POST",
      headers: headers,
      body: form
    })
    .then(r => {
      if(r.status === 201){
        console.log("File uploaded successfully:");
        r.json().then((d) => {
          setTimeout(() => CheckStatus(d["job_id"]), 5000);
        });
      }else if(r.status === 401 || r.status === 403){
        console.error("Unauthorized. Please log in again.");
        setProgressText("Token Expired. Please wait, app will automatically redirect to login.");
        setTimeout(() => {
          setLoader(false);
          Auth.redirectUserLogin();
        }, 1000);
      }else{
        r.json().then((d) => {
          console.log("Response data:", d);
        });
        setLoader(false);
      }
    })
    .catch(console.error);
  };

  return (
    <div className="main-div">
      <ef-loader class="loader" hidden={!loading}></ef-loader>
      <ef-dialog id="dlg1" header="Empty Cache" className="clear-cache-dialog">
        <p>Are you sure you want to empty the cache?</p>
        <p>By clicking "OK", all cached data will be permanently deleted.</p>
      </ef-dialog>
      <ef-dialog id="upload-confirmation-dlg" header="Upload Confirmation" className="upload-confirm-dialog">
        <p>Selected file: <b>{fileMaps[tabValue]}</b></p>
        <p>Selected Sheets: 
          {fileString.length > 0 ? <b>{sheetMappings(tabValue, fileString)}</b> : <b>None</b>}
        </p>
        <p>Version Upgrade: <b>{versionValue}</b></p>
        <p>Are you sure you want to upload the file?</p>
        <p>By clicking "OK", the file will be uploaded and processed.</p>
      </ef-dialog>
      <ef-layout flex>
        <ef-layout className="left">
          <p id="date_p">Today Jan 13</p>
        </ef-layout>
        <ef-layout className="right">
          <ef-overlay opened={loader} className="overlay" withBackdrop fullscreen noCancelOnOutsideClick noCancelOnEscKey>
            <div className="loader-container">
              <p className="loader-text" id="progress-text">Processing...</p>
              <ef-progress-bar id="progress-bar" value="0" label="0"></ef-progress-bar>
            </div>
          </ef-overlay>
          <ef-button className="clear-cache-button" onClick={clearAllDBStores}>Empty Cache</ef-button>
          <ef-button className="upload-button" onClick={uploadClick}>Upload</ef-button>
        </ef-layout>
      </ef-layout>
      <ef-dialog id="d1" header="Select the sheets to update" className="admin-dialog">
        <ef-radio-button name="tab" checked onClick={() => setTabValue("company_snapshot")}>Company Snapshot</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("events")}>Events</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("filings")}>Filings</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("esg")}>ESG</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("estimates")}>IBES Estimates</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("news")}>News</ef-radio-button>
        {/* <ef-radio-button name="tab" onClick={() => setTabValue("snapshot_quotes")}>Snapshot Quotes</ef-radio-button> */}
        <ef-radio-button name="tab" onClick={() => setTabValue("streaming_quotes")}>Streaming & Snapshot Quotes</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("lipper_funds")}>Lipper Funds</ef-radio-button>
        <ef-radio-button name="tab" onClick={() => setTabValue("ownership")}>Ownership</ef-radio-button>
        <br></br>
        {tabValue === "company_snapshot" && companySnapshotCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "events" && eventCheckBoxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "filings" && filingsCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "esg" && esgCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "estimates" && estimatesCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "news" && newsCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "snapshot_quotes" && snapshotCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "streaming_quotes" && streamingCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "lipper_funds" && lipperFundsCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        {tabValue === "ownership" && ownershipCheckboxes.map((checkbox, index) => (
          <ef-checkbox className="admin-checkboxes" key={index} checked={checkbox.checked} value={checkbox.value}>
            {checkbox.label}
          </ef-checkbox>
        ))}
        <div slot="footer">
          <ef-radio-button
            name="version"
            checked={versionValue === "minor"}
            onClick={() => setVersionValue("minor")}
          >
            Minor Version
          </ef-radio-button>
          <ef-radio-button
            name="version"
            checked={versionValue === "major"}
            onClick={() => setVersionValue("major")}
          >
            Major Version
          </ef-radio-button>
          <ef-button onClick={onNextClick}>Next</ef-button>
        </div>
      </ef-dialog>
      <ef-layout>
        <ef-select id="tabSelect" className="tab-select">
          {Object.entries(fileMaps).map(([key, value], index) => (
            <ef-item key={index} value={key} selected={index === 0}>{value}</ef-item>
          ))}
        </ef-select>
        <efx-grid id="filesGrid" className="adminGrid"></efx-grid>
        <ef-pagination id='pagination' className="pagination"></ef-pagination>
      </ef-layout>
    </div>
  );
}

export default Upload;